/**
 * @author       patrick@zumper.com (Patrick Giddings)
 * @copyright    Copyright (c) 2024, Zumper
 */

import { useLocalStorageState } from '@zcode-ui/hook'
import { Close } from '@zcode-ui/icons'
import { Button } from '@zcode-ui/shadz-ui'
import { ON_SERVER } from '@zcode/nr/es/shared/env'
import { isCypress } from '@zcode/utils'
import { differenceInDays, isPast } from 'date-fns'
import React from 'react'

interface Survey {
  date: number
  isHidden: boolean
  isRemoved: boolean
}

export interface RenterSurveyProps {
  children: string
  href: string
}

const getCurrentTime = () => Date.now()

const differenceInDaysToNow = (date: number) => {
  return differenceInDays(date, getCurrentTime())
}

const maxDaysToHide = 3

export const RenterSurvey = ({ children, href }: RenterSurveyProps) => {
  const [params, setParams] = useLocalStorageState<Survey>('survey_2025', {
    isHidden: false,
    isRemoved: false,
    date: getCurrentTime(),
  })

  if (ON_SERVER || params.isRemoved || isCypress()) {
    return null
  }

  const passedEndDate = isPast(new Date(2025, 8, 15))
  const daysDiff = differenceInDaysToNow(params.date)

  if (passedEndDate || (params.isHidden && daysDiff <= maxDaysToHide)) {
    return null
  }

  const handleClose = () => {
    setParams({
      isHidden: true,
      date: getCurrentTime(),
    })
  }

  const handleNavigate = () => {
    setParams({
      isRemoved: true,
    })
  }

  return (
    <div className="flex items-center bg-blue-900 md:rounded-t-md justify-between p-5">
      <a
        className="decoration-none text-gray-0 text-sm font-bold uppercase leading-[shorter] tracking-[2px]"
        href={href}
        onClick={handleNavigate}
        rel="noopener noreferrer"
        target="_blank"
      >
        {children}
      </a>
      <Button
        aria-label="close survey banner"
        className="h-5 min-w-5 p-0 text-center hover:bg-blue-800"
        onClick={handleClose}
        size="sm"
        variant="ghost"
      >
        <Close fill="#FFFFFF" size={12} strokeWidth={2} />
      </Button>
    </div>
  )
}
